/* eslint-disable no-template-curly-in-string */
import React, { useState, useCallback, useEffect } from "react"

import axios from "axios"
import { detect } from "detect-browser"
import { useStaticQuery, graphql } from "gatsby"
import Image from "gatsby-plugin-sanity-image"
import PropTypes from "prop-types"
import { useGoogleReCaptcha } from "react-google-recaptcha-v3"
import { animateScroll as scroll } from "react-scroll"

import * as styles from "./contest.module.scss"
import Analytics from "../../../helpers/analytics"
import ContestPartners from "../../../helpers/contest-partners"
import DarkColor from "../../../helpers/dark-color"
import TimeZone from "../../../helpers/timezone-offset"
import UrlFunctions from "../../../helpers/url"
import CopyContainer from "../../atoms/copy-container"
import FormField from "../../atoms/form-field"
import Grid from "../../atoms/grid"
import Heading from "../../atoms/heading"
import Logo from "../../atoms/logo"
import StickyIntro from "../../atoms/sticky-intro"
import Wrapper from "../../atoms/wrapper"
import Form from "../../molecules/form"
import FormState from "../../molecules/form-state"

const Contest = ({ contest, finePrint, settings }) => {
  const { site, allSanityCountry, allSanityDictionary } = useStaticQuery(
    graphql`
      {
        site {
          siteMetadata {
            siteUrl
          }
        }
        allSanityCountry {
          nodes {
            abbreviation
            url
          }
        }
        allSanityDictionary {
          nodes {
            key {
              current
            }
            value
          }
        }
      }
    `
  )
  const [formState, setFormState] = useState("")
  const [email, setEmail] = useState("")
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [consent, setConsent] = useState("")
  const [fields, setFields] = useState(
    contest.additional_fields.map((field) => ({
      key: field.slug.current,
      value: "",
    }))
  )
  const [customErrorMessage, setCustomErrorMessage] = useState("")

  const { executeRecaptcha } = useGoogleReCaptcha()

  const handleSubmit = useCallback(async (e) => {
    setFormState("pending")
    let customError = false
    e.preventDefault()
    if (!executeRecaptcha) {
      return
    }
    const token = await executeRecaptcha("submit")
    const { partner } = UrlFunctions.getParams()
    const country = allSanityCountry.nodes.find(
      (c) => c.url === site.siteMetadata.siteUrl
    ).abbreviation
    const customFields = [
      { Key: "ContestID", Value: contest.slug.current },
      { Key: "ContestName", Value: contest.name },
      { Key: "Partner", Value: partner },
      { Key: "Country", Value: country },
    ]

    contest.additional_fields.forEach((field) => {
      const currentField = fields.find((f) => f.key === field.slug.current)
      if (field.type === "textarea") {
        if (
          (field.min && currentField.count < field.min) ||
          (field.max && currentField.count > field.max)
        ) {
          setCustomErrorMessage(
            allSanityDictionary.nodes.find(
              (dictionaryItem) =>
                dictionaryItem.key.current === "error_message_wordcount"
            )?.value
          )
          setFormState("error")
          customError = true
          scroll.scrollToTop()
          return
        }
      }
      customFields.push({ Key: currentField.key, Value: currentField.value })
    })

    if (!customError) {
      axios
        .post("/.netlify/functions/createsend", {
          ListId: contest.cm_id,
          token,
          EmailAddress: email,
          Name: `${firstName} ${lastName}`,
          CustomFields: customFields,
          Resubscribe: true,
          ConsentToTrack: "Yes",
          browser: detect()?.name,
        })
        .then(() => {
          setFormState("success")
          Analytics.triggerEvent(
            "contest_entry",
            {
              contest_id: contest.slug.current,
            },
            "ContestEntry"
          )
        })
        .catch((error) => {
          setCustomErrorMessage("")
          scroll.scrollToTop()
          setFormState("error")
          Analytics.triggerEvent(
            "contest_error",
            {
              contest_id: contest.slug.current,
              error_code: error.response.status,
            },
            "ContestError"
          )
        })
    }
  })

  const partners = ContestPartners(
    allSanityDictionary.nodes.find((item) => item.key.current === "title")
      ?.value,
    contest.partners,
    "name"
  )

  const stringToLiteral = (str, obj) =>
    str.replace(/\${([^}]+)}/g, (_, prop) => obj[prop])

  useEffect(() => {
    if (new Date() > new Date(`${contest.end_date}T23:59:59${TimeZone()}`)) {
      setFormState("closed")
    } else {
      setFormState("open")
    }
  }, [])

  const fieldMapValue = (f, field, e) => {
    if (f.key === field.slug.current) {
      if (field.type === "checkbox") return !f.value
      return e.target.value
    }
    return f.value
  }

  const addRulesLink = (text) => {
    const variable = text.includes("${click_here}") ? "click_here" : "rules"
    return text.split(`$\{${variable}}`).map((section, index) => (
      <>
        {stringToLiteral(section, {
          partners,
        })}
        {text.split(`$\{${variable}}`).length !== index + 1 ? (
          <a
            target="_blank"
            href={`/contest/${contest.slug.current}/rules`}
            rel="noreferrer"
          >
            {
              allSanityDictionary.nodes.find(
                (dictionaryItem) => dictionaryItem.key.current === variable
              )?.value
            }
          </a>
        ) : (
          ""
        )}
      </>
    ))
  }

  return (
    <div>
      <Wrapper container>
        <Grid col={2}>
          <StickyIntro>
            <div className={styles.logo}>
              <Logo light={DarkColor(contest.color)} />
              {contest.partners.map((partner) =>
                (partner.partner.dark_logo || partner.partner.logo) &&
                partner.type !== "promotion" ? (
                  <Logo
                    logo={
                      DarkColor(contest.color)
                        ? partner.partner.dark_logo || partner.partner.logo
                        : partner.partner.logo
                    }
                    alt={partner.name}
                    url={partner.url}
                  />
                ) : null
              )}
            </div>
            <Heading>{contest.name}</Heading>
            <Heading level="6">{contest.abstract}</Heading>
            <Image
              {...contest.image}
              width={600}
              className={`${styles.image} ${styles.image__mobile}`}
              alt={contest.name}
            />
            <FormState
              condition={
                formState && formState !== "success" && formState !== "closed"
              }
            >
              <Form
                handleSubmit={handleSubmit}
                errorMessage={
                  customErrorMessage ||
                  allSanityDictionary.nodes.find(
                    (dictionaryItem) =>
                      dictionaryItem.key.current === "error_message"
                  )?.value
                }
                submitText={contest.submit_cta}
                formState={formState}
                buttonVariant={
                  contest.color === "chartruse" ? "secondary" : "primary"
                }
                trackSubscribe
              >
                <FormField
                  label={
                    allSanityDictionary.nodes.find(
                      (dictionaryItem) => dictionaryItem.key.current === "email"
                    )?.value
                  }
                  form="contest"
                  field="email"
                  type="email"
                  value={email}
                  changeEvent={(e) => {
                    setEmail(e.target.value)
                  }}
                  required
                  light={DarkColor(contest.color)}
                />
                <FormField
                  label={
                    allSanityDictionary.nodes.find(
                      (dictionaryItem) =>
                        dictionaryItem.key.current === "first_name"
                    )?.value
                  }
                  form="contest"
                  field="firstName"
                  type="text"
                  value={firstName}
                  changeEvent={(e) => {
                    setFirstName(e.target.value)
                  }}
                  required
                  light={DarkColor(contest.color)}
                />
                <FormField
                  label={
                    allSanityDictionary.nodes.find(
                      (dictionaryItem) =>
                        dictionaryItem.key.current === "last_name"
                    )?.value
                  }
                  form="contest"
                  field="lastName"
                  type="text"
                  value={lastName}
                  changeEvent={(e) => {
                    setLastName(e.target.value)
                  }}
                  required
                  light={DarkColor(contest.color)}
                />
                {contest.additional_fields.map((field) => (
                  <FormField
                    label={field.name}
                    form="subscribe"
                    field={field.slug.current}
                    type={field.type}
                    min={field.min}
                    max={field.max}
                    wordCount={
                      fields.find((f) => f.key === field.slug.current).count
                    }
                    required
                    options={
                      field.type === "select"
                        ? field.values.map((value) => ({ value }))
                        : null
                    }
                    checked={
                      field.type === "checkbox"
                        ? fields.find((f) => f.key === field.slug.current).value
                        : null
                    }
                    changeEvent={(e) => {
                      let count = 0
                      if (field.type === "textarea") {
                        const words = e.target.value
                          .replace(/[\t\n\r.?!]/gm, " ")
                          .split(" ")
                        count = words
                          .map((s) => s.trim())
                          .filter((n) => n).length
                      }
                      setFields(
                        fields.map((f) => ({
                          key: f.key,
                          value: fieldMapValue(f, field, e),
                          count: f.key === field.slug.current ? count : f.count,
                        }))
                      )
                    }}
                    value={
                      fields.find((f) => f.key === field.slug.current).value
                    }
                    light={DarkColor(contest.color)}
                  />
                ))}
                <FormField
                  label={addRulesLink(settings.contest_conditions)}
                  form="contest"
                  field="consent"
                  value="consent"
                  type="checkbox"
                  changeEvent={() => {
                    setConsent(!consent)
                  }}
                  checked={consent}
                  light={DarkColor(contest.color)}
                  required
                />
              </Form>
              <CopyContainer
                className={styles.details}
                bg={contest.color?.replaceAll("-", "")}
              >
                <i>{addRulesLink(finePrint)}</i>
              </CopyContainer>
            </FormState>
            <FormState condition={formState === "success"}>
              <Heading level={3}>{contest.thank_you_title}</Heading>
              <CopyContainer content={contest._rawThankYouMessage} />
            </FormState>
            <FormState condition={formState === "closed"}>
              <CopyContainer content={contest._rawClosedMessage}>
                {!contest._rawClosedMessage
                  ? stringToLiteral(
                      new Date() >
                        new Date(`${contest.winner_date}T00:00:00${TimeZone()}`)
                        ? settings.contest_closed_after
                        : settings.contest_closed,
                      {
                        winner_date: new Intl.DateTimeFormat("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "2-digit",
                        }).format(
                          new Date(`${contest.winner_date}T00:00:00.000`)
                        ),
                      }
                    )
                  : ""}
              </CopyContainer>
            </FormState>
          </StickyIntro>
          <div>
            <Image
              {...contest.image}
              width={600}
              className={`${styles.image} ${styles.image__desktop}`}
              alt={contest.name}
            />
            <CopyContainer
              content={contest._rawDetails}
              bg={contest.color}
              className={styles.description}
            />
          </div>
        </Grid>
      </Wrapper>
    </div>
  )
}

export default Contest

Contest.propTypes = {
  contest: PropTypes.object.isRequired,
  finePrint: PropTypes.string.isRequired,
  settings: PropTypes.object.isRequired,
}
